import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import claim from "../GiftClaim/GiftClaim.module.css";
import styles from "./GiftReady.module.css";
import Api from "../../utils/Api/Api";
import { track, EVENTS } from "../../utils/analyticsService";
import { longDate } from "../../utils/format";
import { TopBar } from "../../components/TopBar/TopBar";
import { Sheet } from "../../components/Sheet/Sheet";
import {
  canNativeShare,
  clearGiftDraft,
  copyText,
  durationText,
  firstName,
  isDiwaliGift,
  markGiftCreated,
  whatsAppUrl,
} from "../../utils/gift";
import FleekIcon from "../../asset/icons/Brand_logo_Sq.png";

/*
 * After a gift is paid for: in place of the usual receipt, the thing the
 * buyer came to do - send it. The web twin of Android's GiftReady.kt.
 *
 * The payment is confirmed by the time this shows. The gift record can still
 * say awaiting_payment for a few seconds, until the gateway's webhook reaches
 * the backend, so the first reads are polled rather than trusted.
 */

/** Webhook lag is seconds; this gives it about half a minute. */
export const GIFT_POLLS = 12;
const GIFT_POLL_MS = 2500;

// App's axios interceptor resolves a failed request with the error.
const outcome = (res) => {
  const failed = !res || res instanceof Error || !!res.isAxiosError;
  const response = failed ? res?.response : res;
  return {
    ok: !failed && !!response && response.status >= 200 && response.status < 300,
    status: response?.status || 0,
    data: response?.data || null,
  };
};

const Diya = () => (
  <svg className={claim.diya} width="40" height="34" viewBox="0 0 40 34" aria-hidden="true">
    <path d="M20 2c3 5 5 8 5 11a5 5 0 0 1-10 0c0-3 2-6 5-11z" fill="#ffc747" />
    <path d="M20 8c1.4 2.4 2.2 4 2.2 5.4a2.2 2.2 0 0 1-4.4 0c0-1.4.8-3 2.2-5.4z" fill="#ff8a66" />
    <path d="M3 20h34c-1.5 7-8.5 12-17 12S4.5 27 3 20z" fill="#e8a317" />
    <path d="M3 20h34" stroke="#ffc747" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const GiftIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 12v9H4v-9" />
    <path d="M2 7h20v5H2z" />
    <path d="M12 21V7" />
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.4 8.4 0 0 1-12.4 7.4L3 21l2.1-5.4A8.4 8.4 0 1 1 21 11.5z" />
  </svg>
);

/** The gift, as the person receiving it will see it. */
const GiftCard = ({ gift }) => {
  const plan = gift?.plan || {};
  const recipient = String(gift?.recipient_name || "").trim();
  const sender = String(gift?.sender_name || "").trim();
  const name = plan.plan_name && plan.plan_name !== plan.brand ? plan.plan_name : "";
  // "Premium - 6 Months" already says how long; don't say it twice.
  const length = durationText(plan);
  const saysLength = length && name.toLowerCase().includes(length.replace(/s$/, "").toLowerCase());
  const sub = [name, saysLength ? "" : length].filter(Boolean).join(" · ");
  // Plain unless the gift was created with the older Diwali theme.
  const diwali = isDiwaliGift(gift?.theme);
  return (
    <section className={claim.card}>
      <div className={claim.hero}>
        {diwali && <Diya />}
        <p className={claim.eyebrow}>{diwali ? "A Diwali gift" : "A gift"}</p>
        <h2 className={claim.headline}>{recipient ? `For ${recipient}` : plan.brand || "Your gift"}</h2>
      </div>
      <div className={claim.body}>
        <div className={claim.planRow}>
          <img
            className={claim.logo}
            src={plan.logo_url || FleekIcon}
            alt=""
            onError={(e) => {
              e.currentTarget.src = FleekIcon;
            }}
          />
          <div className={claim.planText}>
            <div className={claim.brand}>{plan.brand}</div>
            {sub && <div className={claim.planName}>{sub}</div>}
          </div>
        </div>
        {gift?.message && (
          <blockquote className={claim.message}>
            <p>{gift.message}</p>
            {sender && <footer>— {sender}</footer>}
          </blockquote>
        )}
        {!gift?.message && sender && <p className={styles.from}>From {sender}</p>}
      </div>
    </section>
  );
};

/**
 * `orderId` is the placed order's UUID (PlacedOrderV2.id) - what
 * /gifts/v1/order/<uuid> takes - not the short payment reference shown to
 * the buyer. gift_created is deduped on it too.
 */
export const GiftReady = ({ orderId, orderItemId = null, pollMs = GIFT_POLL_MS }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading | gift | failed
  const [gift, setGift] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmKeep, setConfirmKeep] = useState(false);
  const [keeping, setKeeping] = useState(false);
  const [keepError, setKeepError] = useState("");
  const [kept, setKept] = useState(false);
  const runRef = useRef(0);

  // Paid for: the draft written on the plan page has done its job.
  useEffect(() => {
    clearGiftDraft();
  }, []);

  const load = useCallback(async () => {
    const run = ++runRef.current;
    setPhase("loading");
    for (let attempt = 0; attempt < GIFT_POLLS; attempt += 1) {
      const { ok, data } = outcome(await Api.GetGiftForOrder(orderId).catch((e) => e));
      if (run !== runRef.current) return;
      if (ok && data?.state && data.state !== "awaiting_payment") {
        // Counted once the gift exists as something that can be sent, not at
        // payment, and once per order however often this screen reloads.
        if (markGiftCreated(orderId)) track(EVENTS.GIFT_CREATED, { theme: data.theme || null });
        setGift(data);
        setPhase("gift");
        return;
      }
      if (attempt < GIFT_POLLS - 1) {
        await new Promise((r) => setTimeout(r, pollMs));
        if (run !== runRef.current) return;
      }
    }
    setPhase("failed");
  }, [orderId, pollMs]);

  useEffect(() => {
    load();
    return () => {
      runRef.current += 1; // cancels a poll in flight
    };
  }, [load]);

  const link = gift?.link || gift?.links?.app || "";
  const shareText = gift?.share_message || link;

  const sendWhatsApp = () => {
    track(EVENTS.GIFT_SHARED, { via: "whatsapp" });
    window.open(whatsAppUrl(shareText), "_blank", "noopener,noreferrer");
  };

  const shareNative = async () => {
    try {
      await navigator.share({ text: shareText });
      track(EVENTS.GIFT_SHARED, { via: "share" });
    } catch {
      /* dismissed */
    }
  };

  const copyLink = async () => {
    if (await copyText(link)) {
      track(EVENTS.GIFT_SHARED, { via: "copy" });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const keep = async () => {
    if (!gift?.token || keeping) return;
    setConfirmKeep(false);
    setKeeping(true);
    setKeepError("");
    const { ok, data } = outcome(await Api.KeepGift(gift.token).catch((e) => e));
    setKeeping(false);
    if (ok) {
      track(EVENTS.GIFT_KEPT, { theme: gift.theme || null });
      setKept(true);
      return;
    }
    setKeepError(data?.detail || "We couldn't do that. Try again.");
    // Claimed or returned in the meantime: show what it is now.
    if (data?.error === "not_keepable") {
      const fresh = outcome(await Api.GetGiftForOrder(orderId).catch((e) => e));
      if (fresh.ok && fresh.data) setGift(fresh.data);
    }
  };

  const viewOrder = () => navigate(orderItemId ? `/orderdetail/${orderItemId}` : "/myorder");
  const done = () => navigate("/marketplace");

  const state = gift?.state;
  const claimer = firstName(gift?.claimed_by_name);
  const returnsOn = gift?.returns_at ? longDate(gift.returns_at) : "";

  const endings = {
    claimed: {
      title: claimer ? `${claimer} claimed your gift` : "Your gift has been claimed",
      body: "It's theirs now. Nothing more to do.",
    },
    returned: {
      title: "It came back to you",
      body: "Nobody claimed it, so the plan is yours. Find it in your orders.",
    },
    expired: {
      title: "This gift link has expired",
      body: "Nobody claimed it in time, so the plan is coming back to you.",
    },
  };

  return (
    <div className={`${claim.page} ${isDiwaliGift(gift?.theme) ? claim.diwali : ""}`}>
      <TopBar title="Your gift" backTo="/marketplace" />
      <div className={claim.shell}>
        {phase === "loading" && (
          <section className={claim.statusCard} aria-busy="true">
            <span className={claim.statusIcon}>
              <span className={styles.spinner} />
            </span>
            <h1 className={claim.statusTitle} role="status">Wrapping your gift…</h1>
            <p className={claim.statusBody}>Your payment went through. This takes a few seconds.</p>
          </section>
        )}

        {phase === "failed" && (
          <section className={claim.statusCard}>
            <span className={claim.statusIcon}>
              <GiftIcon />
            </span>
            <h1 className={claim.statusTitle}>Your gift is paid for</h1>
            <p className={claim.statusBody}>We couldn't load its link just now. Try again, or find it in your orders.</p>
            <button type="button" className={claim.secondary} onClick={load}>
              Try again
            </button>
            <button type="button" className={claim.textButton} onClick={viewOrder}>
              View my orders
            </button>
          </section>
        )}

        {phase === "gift" && kept && (
          <section className={claim.statusCard}>
            <span className={claim.statusIcon}>
              <GiftIcon />
            </span>
            <h1 className={claim.statusTitle}>It's yours</h1>
            <p className={claim.statusBody}>The gift link no longer works. The plan is in your orders.</p>
            <button type="button" className={claim.secondary} onClick={viewOrder}>
              View order
            </button>
          </section>
        )}

        {phase === "gift" && !kept && (
          <>
            <h1 className={styles.title}>{state === "ready" ? "Your gift is ready" : endings[state]?.title || "Your gift"}</h1>
            <p className={styles.lede}>
              {state === "ready"
                ? "Send them the link. When they claim it, the plan is theirs."
                : endings[state]?.body || "You'll find it in your orders."}
            </p>

            <GiftCard gift={gift} />

            {state === "ready" && shareText ? (
              <div className={styles.actions}>
                <button type="button" className={`${claim.primary} ${styles.primary}`} onClick={sendWhatsApp}>
                  <WhatsAppIcon />
                  Send on WhatsApp
                </button>
                <div className={styles.row}>
                  {link && (
                    <button type="button" className={styles.secondaryBtn} onClick={copyLink}>
                      {copied ? "Link copied" : "Copy link"}
                    </button>
                  )}
                  {canNativeShare() && (
                    <button type="button" className={styles.secondaryBtn} onClick={shareNative}>
                      More ways to share
                    </button>
                  )}
                </div>
                <p className={claim.fine}>
                  The plan, and any code that comes with it, goes to whoever claims this link.
                  {returnsOn ? ` Not claimed by ${returnsOn}? It comes back to you.` : ""}
                </p>
                {keepError && (
                  <p className={claim.error} role="alert">
                    {keepError}
                  </p>
                )}
                <button type="button" className={styles.quiet} onClick={() => setConfirmKeep(true)} disabled={keeping}>
                  {keeping ? "Keeping…" : "Keep it for myself"}
                </button>
              </div>
            ) : (
              <div className={styles.actions}>
                <button type="button" className={`${claim.primary} ${styles.primary}`} onClick={viewOrder}>
                  View order
                </button>
              </div>
            )}

            <button type="button" className={`${claim.textButton} ${styles.done}`} onClick={done}>
              Done
            </button>
          </>
        )}
      </div>

      <Sheet open={confirmKeep} onClose={() => setConfirmKeep(false)} label="Keep this gift?">
        <p className={styles.sheetTitle}>Keep this gift?</p>
        <p className={styles.sheetBody}>The link stops working and the plan becomes yours. This can't be undone.</p>
        <button type="button" className={`${claim.primary} ${styles.primary}`} onClick={keep}>
          Keep it
        </button>
        <button type="button" className={styles.secondaryBtn} onClick={() => setConfirmKeep(false)}>
          Cancel
        </button>
      </Sheet>
    </div>
  );
};

export default GiftReady;
